import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { authStrategy } from '@/lib/auth/strategy';

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
if (!apiBaseUrl) {
  console.warn('VITE_API_BASE_URL is not set — API requests will go to the current origin.');
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  timeout: 60_000,
  headers: { 'Content-Type': 'application/json' },
  /*
   * Credentials mode is strategy-driven:
   *   - Bearer strategy → false. Required because java_backend responds
   *     with `Access-Control-Allow-Origin: *` and browsers refuse a
   *     wildcard origin when credentials mode is on.
   *   - httpOnly-cookie strategy → true. obd3-api will respond with an
   *     explicit `Access-Control-Allow-Origin: <our origin>` plus
   *     `Access-Control-Allow-Credentials: true`, satisfying the browser.
   */
  withCredentials: authStrategy.needsCredentials,
});

apiClient.interceptors.request.use(async (config) => {
  const token = authStrategy.getAccessToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Attach HMAC panel signature for req_src tracking
  const { getPanelHeaders } = await import('./panel-sig');
  const panelHeaders = await getPanelHeaders();
  Object.entries(panelHeaders).forEach(([k, v]) => {
    config.headers[k] = v;
  });

  return config;
});

apiClient.interceptors.response.use(
  (res) => res,
  (error: AxiosError<{ message?: string }>) => {
    // Token expired or invalid → wipe local session and bounce to /login.
    // We deliberately mirror the legacy semantics (clear-and-redirect on 401)
    // and only treat token-related 401s as session-ending.
    if (error.response?.status === 401) {
      const message = error.response.data?.message;
      const tokenInvalid = message === 'Token Not Valid.' || message === 'Token Expired';
      if (tokenInvalid) {
        authStrategy.clearSession();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  },
);

/** Convenience helpers mirroring the legacy Fetcher surface. */
export const api = {
  get: <T,>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config).then((r) => r.data),
  post: <T,>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, data, config).then((r) => r.data),
  put: <T,>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, data, config).then((r) => r.data),
  patch: <T,>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, data, config).then((r) => r.data),
  delete: <T,>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config).then((r) => r.data),
};
